import { ReactElement, createContext, useContext } from 'react';
import { Location, NavigateProps, OutletProps, TNavigateFunction } from './types';
import { DefaultOutlet } from './DefaultOutlet';
import { DefaultNavigate } from './DefaultNavigate';

const defaultNavigate: TNavigateFunction = () => {
  throw new Error('JXT router not provided');
};
const defaultLocation: Location = {
  pathname: '',
  search: '',
  hash: '',
  state: null,
  key: '',
};
function compilePath(
  path: string,
  caseSensitive = false,
  end = true
): [RegExp, { paramName: string; isOptional?: boolean }[]] {
  console.warn(
    path === '*' || !path.endsWith('*') || path.endsWith('/*'),
    `Route path "${path}" will be treated as if it were "${path.replace(/\*$/, '/*')}" because the \`*\` character must always follow a \`/\` in the pattern. To get rid of this warning, please change the route path to "${path.replace(/\*$/, '/*')}".`
  );
  const params = [];
  let regexpSource =
    '^' +
    path
      .replace(/\/*\*?$/, '')
      .replace(/^\/*/, '/')
      .replace(/[\\.*+^${}|()[\]]/g, '\\$&')
      .replace(/\/:([\w-]+)(\?)?/g, (_, paramName, isOptional) => {
        params.push({ paramName, isOptional: isOptional != null });
        return isOptional ? '/?([^\\/]+)?' : '/([^\\/]+)';
      });
  if (path.endsWith('*')) {
    params.push({ paramName: '*' });
    regexpSource += path === '*' || path === '/*' ? '(.*)$' : '(?:\\/(.+)|\\/*)$';
  } else if (end) {
    regexpSource += '\\/*$';
  } else if (path !== '' && path !== '/') {
    regexpSource += '(?:(?=\\/|$))';
  } else {
    // noop
  }
  const matcher = new RegExp(regexpSource, caseSensitive ? void 0 : 'i');
  return [matcher, params];
}

const defaultMatchPath = function matchPath<Path extends string>(
  pattern: string | { path: string; caseSensitive?: boolean; end?: boolean },
  pathname: string
): PathMatch<ParamParseKey<Path>> | null {
  if (typeof pattern === 'string') {
    pattern = { path: pattern, caseSensitive: false, end: true };
  }
  const [matcher, compiledParams] = compilePath(pattern.path, pattern.caseSensitive, pattern.end);
  const match = pathname.match(matcher);
  if (!match) return null;
  const matchedPathname = match[0];
  let pathnameBase = matchedPathname.replace(/(.)\/+$/, '$1');
  const captureGroups = match.slice(1);
  const params = compiledParams.reduce<{ [key: string]: string | undefined }>(
    (memo2, { paramName, isOptional }, index) => {
      if (paramName === '*') {
        const splatValue = captureGroups[index] || '';
        pathnameBase = matchedPathname.slice(0, matchedPathname.length - splatValue.length).replace(/(.)\/+$/, '$1');
      }
      const value = captureGroups[index];
      if (isOptional && !value) {
        memo2[paramName] = void 0;
      } else {
        memo2[paramName] = (value || '').replace(/%2F/g, '/');
      }
      return memo2;
    },
    {}
  ) as Params<ParamParseKey<Path>>;

  return {
    params,
    pathname: matchedPathname,
    pathnameBase,
    pattern,
  };
};
const defaultRouterContext = {
  navigate: defaultNavigate,
  matchPath: defaultMatchPath,
  location: defaultLocation,
  Outlet: DefaultOutlet,
  Navigate: DefaultNavigate,
};

export interface PathPattern<Path extends string = string> {
  /**
   * A string to match against a URL pathname. May contain `:id`-style segments
   * to indicate placeholders for dynamic parameters. May also end with `/*` to
   * indicate matching the rest of the URL pathname.
   */
  path: Path;
  /**
   * Should be `true` if the static portions of the `path` should be matched in
   * the same case.
   */
  caseSensitive?: boolean;
  /**
   * Should be `true` if this pattern should match the entire URL pathname.
   */
  end?: boolean;
}

type Regex_az =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z';
type Regez_AZ =
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z';
type Regex_09 = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type Regex_w = Regex_az | Regez_AZ | Regex_09 | '_';
type ParamChar = Regex_w | '-';
type RegexMatchPlus<CharPattern extends string, T extends string> = T extends `${infer First}${infer Rest}`
  ? First extends CharPattern
    ? RegexMatchPlus<CharPattern, Rest> extends never
      ? First
      : `${First}${RegexMatchPlus<CharPattern, Rest>}`
    : never
  : never;
type _PathParam<Path extends string> = Path extends `${infer L}/${infer R}`
  ? _PathParam<L> | _PathParam<R>
  : Path extends `:${infer Param}`
    ? Param extends `${infer Optional}?${string}`
      ? RegexMatchPlus<ParamChar, Optional>
      : RegexMatchPlus<ParamChar, Param>
    : never;
type PathParam<Path extends string> = Path extends '*' | '/*'
  ? '*'
  : Path extends `${infer Rest}/*`
    ? '*' | _PathParam<Rest>
    : _PathParam<Path>;
export type ParamParseKey<Segment extends string> = [PathParam<Segment>] extends [never] ? string : PathParam<Segment>;
/**
 * The parameters that were parsed from the URL path.
 */
type Params<Key extends string = string> = {
  readonly [key in Key]: string | undefined;
};
/**
 * A PathMatch contains info about how a PathPattern matched on a URL pathname.
 */
export interface PathMatch<ParamKey extends string = string> {
  /**
   * The names and values of dynamic parameters in the URL.
   */
  params: Params<ParamKey>;
  /**
   * The portion of the URL pathname that was matched.
   */
  pathname: string;
  /**
   * The portion of the URL pathname that was matched before child routes.
   */
  pathnameBase: string;
  /**
   * The pattern that was used to match.
   */
  pattern: PathPattern;
}

export const JxtRouterContext = createContext<{
  navigate: TNavigateFunction;
  matchPath: <Path extends string>(pattern: PathPattern<Path> | Path, pathname: string) => PathMatch<ParamParseKey<Path>> | null;
  location: Location;
  Outlet?: (props: OutletProps) => ReactElement | null;
  Navigate?: ({ to, replace, state, relative }: NavigateProps) => null;
}>(defaultRouterContext);

export const useJxtRouter = () => {
  return useContext(JxtRouterContext);
};
